import { Fragment } from 'react/jsx-runtime';

import { logError } from 'lib/sentry/logError';
import { VStack } from 'shared';

import {
  GoalBreakdownChallengeCardMenu,
  GoalChallengeCard,
  GoalFocusArea,
  IGoal,
  useGoalBreakdown,
  useGoalQueryActions,
  useMoveGoalBreakdownChallenges,
} from 'features/goal';
import { useMoveGoalBreakdownChallengesMutation } from 'features/goal/hooks/mutation/useMoveGoalBreakdownChallenge';

import { Droppable } from 'shared/components/DragNDrop/Droppable';
import { Sortable } from 'shared/components/DragNDrop/Sortable';
import { showErrorToast } from 'shared/components/Toast';

export const GoalBreakdownBoardFocusAreas = () => {
  const { focusAreas } = useGoalBreakdown();

  const {
    cancelGoalQueries,
    getGoalQuery,
    updateGoalQueryData,
    invalidateGoalQuery,
  } = useGoalQueryActions();

  const { mutate: mutateChallenges } = useMoveGoalBreakdownChallengesMutation({
    options: {
      onMutate: async () => {
        await cancelGoalQueries();

        const previousValue = getGoalQuery();

        return { previousValue };
      },
      onError: async (
        error: any,
        __: any,
        context: { previousValue: IGoal } | undefined
      ) => {
        if (!context?.previousValue) {
          return;
        }

        updateGoalQueryData((_: IGoal) => context.previousValue);

        showErrorToast('Failed to move challenge');
        logError(error);
      },
      onSettled: () => {
        invalidateGoalQuery();
      },
    },
  });

  const { moveToFocusArea, moveToChallenges } =
    useMoveGoalBreakdownChallenges();

  return (
    <VStack gap="var(--spacing01)">
      {focusAreas.map((focusArea, focusAreaIndex) => (
        <Fragment key={focusArea.focusAreaId}>
          <Droppable
            key={`${focusArea.focusAreaId}-${focusAreaIndex}`}
            acceptType={['challenge']}
            onDrop={({ id, index }) =>
              mutateChallenges({
                customChallengeId: id,
                index,
                focusAreaId: focusArea.focusAreaId,
              })
            }
            onHover={(draggedItem: {
              id: string;
              focusAreaIndex: number;
              index: number;
            }) => moveToFocusArea(focusAreaIndex)(draggedItem)}
            height="auto"
          >
            <GoalFocusArea
              title={focusArea.title}
              focusAreaId={focusArea.focusAreaId}
              key={focusArea.focusAreaId}
            >
              <VStack gap="var(--spacing01)" alignItems="stretch">
                {focusArea.challenges.map((challenge, challengeIndex) => (
                  <Fragment
                    key={`${focusArea.focusAreaId}-${challenge.challengeId}`}
                  >
                    <Sortable
                      key={`${focusArea.focusAreaId}-${focusAreaIndex}-${challenge.challengeId}`}
                      item={{
                        type: 'challenge',
                        id: challenge.challengeId,
                        index: challengeIndex,
                        focusAreaIndex,
                      }}
                      onHover={(draggedItem) =>
                        moveToChallenges(focusAreaIndex)(draggedItem, {
                          id: challenge.challengeId,
                          focusAreaIndex,
                        })
                      }
                      onDrop={({ id, index }) => {
                        mutateChallenges({
                          customChallengeId: id,
                          index,
                          focusAreaId: focusArea.focusAreaId,
                        });
                      }}
                    >
                      <GoalChallengeCard challenge={challenge}>
                        <GoalBreakdownChallengeCardMenu challenge={challenge} />
                      </GoalChallengeCard>
                    </Sortable>
                  </Fragment>
                ))}
              </VStack>
            </GoalFocusArea>
          </Droppable>
        </Fragment>
      ))}
    </VStack>
  );
};
